/**
 * AI 调用日志页面
 */

import React, { useState, useEffect } from 'react';
import {
  Space,
  Select,
  DatePicker,
  Button,
  Tag,
  Descriptions,
  message,
  Input,
} from 'antd';
import { ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import { ContainerPanel, CompsTable, ContainerModal } from '@ms/shared-components';
import dayjs from '../../utils/dayjsConfig';
import { aiLogService } from '../../services/aiLogService';
import type { AiCallLog } from '../../types';

const { RangePicker } = DatePicker;
const { Option } = Select;

const LogsPage: React.FC = () => {
  const [logs, setLogs] = useState<AiCallLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState<{
    status?: string;
    serviceName?: string;
    startDate?: string;
    endDate?: string;
  }>({});
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AiCallLog | null>(null);

  // 加载日志列表
  const loadLogs = async () => {
    setLoading(true);
    try {
      const result = await aiLogService.getLogs({
        page: pagination.page,
        pageSize: pagination.pageSize,
        ...filters,
      });
      setLogs(result.logs);
      setPagination((prev) => ({
        ...prev,
        total: result.pagination.total,
      }));
    } catch (error: any) {
      message.error(error.message || '加载日志失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize, filters]);

  // 查看详情
  const handleViewDetail = async (log: AiCallLog) => {
    try {
      const detail = await aiLogService.getLogById(log.id);
      setSelectedLog(detail);
      setDetailModalVisible(true);
    } catch (error: any) {
      message.error(error.message || '加载详情失败');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '请求 ID',
      dataIndex: 'requestId',
      key: 'requestId',
      ellipsis: true,
      width: 200,
    },
    {
      title: '服务名称',
      dataIndex: 'serviceName',
      key: 'serviceName',
      width: 140,
      render: (name?: string) => name || '-',
    },
    {
      title: '模型',
      dataIndex: 'model',
      key: 'model',
      width: 200,
      render: (_: any, record: AiCallLog) => (
        <Space>
          <Tag color="blue">{record.provider || '-'}</Tag>
          {record.model || '-'}
        </Space>
      ),
    },
    {
      title: '访问标签',
      dataIndex: 'accessTag',
      key: 'accessTag',
      width: 110,
      render: (tag?: string) => tag ? <Tag>{tag}</Tag> : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: string) => (
        <Tag color={status === 'success' ? 'green' : 'red'}>
          {status === 'success' ? '成功' : '失败'}
        </Tag>
      ),
    },
    {
      title: 'Token 使用',
      dataIndex: 'tokensUsed',
      key: 'tokensUsed',
      width: 110,
      render: (tokens?: number) => tokens || '-',
    },
    {
      title: '耗时',
      dataIndex: 'duration',
      key: 'duration',
      width: 90,
      render: (duration?: number) => duration ? `${duration}ms` : '-',
    },
    {
      title: '时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a: AiCallLog, b: AiCallLog) =>
        dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: any, record: AiCallLog) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          详情
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <ContainerPanel
        title="AI 调用日志"
        buttons={[
          {
            key: 'refresh',
            icon: <ReloadOutlined />,
            type: 'text',
            onClick: loadLogs,
            title: '刷新',
            showTextOnHover: true,
          },
        ]}
      >
        {/* 筛选器 */}
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            placeholder="状态"
            allowClear
            style={{ width: 120 }}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, status: value }))
            }
          >
            <Option value="success">成功</Option>
            <Option value="error">失败</Option>
          </Select>

          <Input
            placeholder="服务名称"
            style={{ width: 150 }}
            allowClear
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                serviceName: e.target.value || undefined,
              }))
            }
          />

          <RangePicker
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                const start = dayjs(dates[0]);
                const end = dayjs(dates[1]);
                if (start.isValid() && end.isValid()) {
                  setFilters((prev) => ({
                    ...prev,
                    startDate: start.format('YYYY-MM-DD'),
                    endDate: end.format('YYYY-MM-DD'),
                  }));
                } else {
                  setFilters((prev) => ({
                    ...prev,
                    startDate: undefined,
                    endDate: undefined,
                  }));
                }
              } else {
                setFilters((prev) => ({
                  ...prev,
                  startDate: undefined,
                  endDate: undefined,
                }));
              }
            }}
          />
        </Space>

        <CompsTable
          resizableColumns
          storageKey="msai-logs"
          tableProps={{
            columns,
            dataSource: logs,
            rowKey: 'id',
            loading,
            pagination: {
              current: pagination.page,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, pageSize) => {
                setPagination((prev) => ({
                  ...prev,
                  page,
                  pageSize: pageSize || prev.pageSize,
                }));
              },
            },
          }}
        />
      </ContainerPanel>

      <ContainerModal
        title="调用日志详情"
        visible={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={800}
        height="auto"
        contentPadding={{ vertical: 24, horizontal: 16 }}
        footerRightButtons={[
          {
            key: 'close',
            text: '关闭',
            type: 'default',
            onClick: () => setDetailModalVisible(false),
          },
        ]}
        modalProps={{ destroyOnClose: true }}
      >
        {selectedLog && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="请求 ID" span={2}>
              {selectedLog.requestId}
            </Descriptions.Item>
            <Descriptions.Item label="服务名称">
              {selectedLog.serviceName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={selectedLog.status === 'success' ? 'green' : 'red'}>
                {selectedLog.status === 'success' ? '成功' : '失败'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="提供商">
              {selectedLog.provider || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="模型">
              {selectedLog.model || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="访问标签">
              {selectedLog.accessTag || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="访问类型">
              {selectedLog.accessType || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Token 使用">
              {selectedLog.tokensUsed || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="耗时">
              {selectedLog.duration ? `${selectedLog.duration}ms` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="提示词" span={2}>
              <div
                style={{
                  maxHeight: 200,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {selectedLog.prompt || '-'}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="响应" span={2}>
              <div
                style={{
                  maxHeight: 200,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {selectedLog.response || '-'}
              </div>
            </Descriptions.Item>
            {selectedLog.errorMsg && (
              <Descriptions.Item label="错误信息" span={2}>
                <div style={{ color: 'red' }}>{selectedLog.errorMsg}</div>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="创建时间" span={2}>
              {dayjs(selectedLog.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </ContainerModal>
    </div>
  );
};

export default LogsPage;
